import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DialogTitle, DialogContent, Box, Typography, LinearProgress, Button } from '@mui/material';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Theme from 'theme';
import { MixpanelEventName } from 'utils/constants';
import { useMutation } from 'react-query';
import { checkoutSession } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import { ToastProps } from 'components/Common/Toast';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '', {});
interface PaymentInfoProps {
  onNext: () => void;
  step: number;
  totalSteps: number;
  setToastProps?: (props: ToastProps | null) => void;
  token?: string;
}

function PaymentInfo({
  onNext, step, totalSteps, setToastProps, token,
}: PaymentInfoProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const trialDays = searchParams.get('freetrialdays') || '';
  const trialCode = searchParams.get('freetrialcode') || 'dp4Wa4AE36X8mT8';
  const referralCode = searchParams.get('freetrialreferralcode') || '';
  const eventProps = useMemo(() => ({
    trial_days: trialDays,
    trial_code: trialCode,
    referral_code: referralCode,
  }), [trialDays, trialCode, referralCode]);

  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [paymentComplete, setPaymentComplete] = useState(false);

  React.useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_PAYMENT_DIALOG_VIEWED, eventProps);
  }, [eventProps]);

  const { mutate } = useMutation(
    (payload: {
      price_id: string, trialDaysCode:
      string
    }) => checkoutSession(http.post, payload, token),
    {
      onSuccess: (data) => {
        setClientSecret(data.sessionId);
        trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_PAYMENT_SUCCESS, eventProps);
      },
      onError: (error) => {
        setToastProps?.({ message: (error as Error).message, open: true, type: 'error' });
        trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_PAYMENT_ERROR, eventProps);
        logSentryError(`Error creating checkout session - ${(error as Error)?.message}`);
      },
      retry: 3,
    },
  );

  React.useEffect(() => {
    mutate({ price_id: 'prod_SyURP9aZAL9ZyN', trialDaysCode: trialCode });
  }, [mutate, trialCode]);

  const options = {
    clientSecret,
    onComplete: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_STRIPE_PAYMENT_COMPLETED, eventProps);
      setPaymentComplete(true);
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_PAYMENT_CONTINUE_BUTTON_CLICKED, eventProps);
    onNext();
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0, pl: 3, pt: 2 }}>
        Payment info
      </DialogTitle>
      <DialogContent sx={{ pt: 4, pl: 2, pr: 2, minHeight: 500 }}>
        <LinearProgress
          variant='determinate'
          value={(step / totalSteps) * 100}
          color='success'
          sx={{
            width: '100%',
            borderRadius: '24px 24px 0 0',
            height: 4,
          }}
        />
        <Typography
          px={3}
          pt={1}
          pb={2}
          fontWeight={400}
          color={Theme.custom.colors.lightTextPrimary}
          fontSize={24}
        >
          You won’t be charged until after your free trial ends.
        </Typography>
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={options}
        >
          <Box
            sx={{
              py: 3,
              background: clientSecret ? '#1e2731' : 'transparent',
            }}
          >
            <EmbeddedCheckout />
          </Box>
        </EmbeddedCheckoutProvider>
      </DialogContent>
      {paymentComplete && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 3, pb: 3 }}>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            sx={{
              py: 1.5,
              px: 6,
              background: Theme.custom.colors.lightPrimaryMain,
              color: Theme.custom.colors.white,
              fontSize: 15,
              borderRadius: 8,
              boxShadow: 'none',
              mb: 2,
            }}
            onClick={handleSubmit}
          >
            CONTINUE
          </Button>
        </Box>
      )}
    </>
  );
}

export default PaymentInfo;
